// frontend/src/pages/asset-admin/AssetAdminFleetInspections.js
/**
 * Asset Admin — Fleet Inspections
 *
 * Custodian view of the monthly vehicle inspection log:
 *   1. Compliance-calendar grid — who's done / due / overdue / flagged this month
 *   2. Exceptions queue — flagged items across all periods, needs custodian follow-up
 *   3. Detail drill-down — full checklist, remarks, and driver-attached fault photos
 *
 * Notify-only model: flagging an item alerts the custodian here — it never
 * auto-creates a workshop transfer. Send to Workshop stays a deliberate,
 * separate action from the vehicle's My Assets card.
 *
 * Data: GET /asset-inventory/vehicle-inspections/due-status/, /exceptions/, /{id}/
 * Theme: Red (Admin). Follows the design-system-reference metric/panel patterns.
 */

import React, { useMemo, useState } from 'react';
import {
  ClipboardCheck, AlertTriangle, CheckCircle2, Clock3, Calendar, Image as ImageIcon,
  ChevronRight, AlertCircle, User, Gauge, History,
} from 'lucide-react';
import PillSelect from '../../components/UI/PillSelect';
import { CountBadge } from '../../components/UI/CountBadge';
import { StatusBadge } from '../../components/UI/StatusBadge';
import VehicleInspectionDetailModal from '../../components/asset/VehicleInspectionDetailModal';
import { formatDate } from '../../utils/dateUtils';
import {
  useInspectionsDueStatus,
  useInspectionExceptions,
} from '../../hooks/useAssetInventoryData';
import { useAssetDetail } from '../../contexts/AssetDetailContext';

/**
 * Ops triage order for the compliance calendar.
 * Not an activity feed — do NOT sort by newest last-done first (that buries neglect).
 * 1) Status urgency: breaches/faults before chase, completed last
 * 2) Within a bucket: longest since last inspection (never → oldest → newest)
 * 3) Plate A–Z as a stable tiebreak
 */
const STATUS_SORT = { overdue: 0, flagged: 1, due: 2, upcoming: 3, done: 4 };

/** Epoch ms for last_done; missing/invalid sorts as oldest (never inspected). */
const lastDoneSortKey = (date) => {
  if (!date) return 0;
  const t = Date.parse(date);
  return Number.isFinite(t) ? t : 0;
};

const formatKm = (km) => {
  if (km == null || km === '') return null;
  const n = Number(km);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString('en-SG')} km`;
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

/** Section chrome: title + optional solid CountBadge inline (DS panel-header totals). */
const Panel = ({ title, count, icon: Icon, children, action }) => (
  <div className="bg-white rounded-2xl shadow-sm border">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        <span className="truncate">{title}</span>
        {count != null && (
          <CountBadge
            count={count}
            theme="admin"
            size="md"
            variant="solid"
            max={9999}
            className="flex-shrink-0"
          />
        )}
      </h3>
      {action}
    </div>
    <div className="p-4 sm:p-6">
      {children}
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen animate-pulse">
    <div className="bg-white rounded-2xl shadow-lg p-6 h-20" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((i) => <div key={i} className="bg-white rounded-2xl border h-24" />)}
    </div>
    <div className="bg-white rounded-2xl border h-80" />
  </div>
);

// Last 12 calendar months, newest first — matches the year-picker pattern on
// the Fleet Maintenance page but at month granularity (inspections are monthly).
const monthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const label = d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
};

const rowClick = (r, { setDetailModal, handleViewVehicle }) => {
  if (r.inspection_id) {
    setDetailModal({ open: true, inspectionId: r.inspection_id });
  } else if (r.odometer_review_inspection_id) {
    setDetailModal({ open: true, inspectionId: r.odometer_review_inspection_id });
  } else {
    handleViewVehicle(r.asset_id);
  }
};

const AssetAdminFleetInspections = () => {
  const months = monthOptions();
  const [period, setPeriod] = useState(months[0].value);
  const { data: dueStatusData, isLoading, error } = useInspectionsDueStatus({ period });
  const { data: exceptions = [], isLoading: loadingExceptions } = useInspectionExceptions();
  const { openAssetDetail } = useAssetDetail();
  const [detailModal, setDetailModal] = useState({ open: false, inspectionId: null });

  const handleViewVehicle = (fleetVehicleAssetId) => {
    if (fleetVehicleAssetId) openAssetDetail(fleetVehicleAssetId);
  };

  const rows = useMemo(
    () => dueStatusData?.rows || [],
    [dueStatusData?.rows],
  );
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => {
      const sa = STATUS_SORT[a.row_status] ?? 9;
      const sb = STATUS_SORT[b.row_status] ?? 9;
      if (sa !== sb) return sa - sb;
      const da = lastDoneSortKey(a.last_done_date);
      const db = lastDoneSortKey(b.last_done_date);
      if (da !== db) return da - db;
      return (a.vehicle_number || '').localeCompare(b.vehicle_number || '');
    }),
    [rows]
  );

  if (isLoading) return <PageSkeleton />;

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load fleet inspections</p>
          <p className="text-red-600 text-sm mt-1">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const total = rows.length;
  const flaggedRows = rows.filter((r) => r.row_status === 'flagged');
  const doneCount = rows.filter((r) => r.row_status === 'done' || r.row_status === 'flagged').length;
  const overdueCount = rows.filter((r) => r.row_status === 'overdue').length;
  const complianceRate = total ? Math.round((doneCount / total) * 100) : 0;
  const clickCtx = { setDetailModal, handleViewVehicle };

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Fleet Inspections</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
                Monthly driver walk-around checklist &amp; compliance calendar
              </p>
            </div>
          </div>
          <PillSelect
            options={months}
            value={period}
            onChange={setPeriod}
            icon={Calendar}
            iconColor="text-red-600"
            theme="admin"
            minWidth="min-w-[180px]"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Compliance Rate"
          value={`${complianceRate}%`}
          subtitle={`${doneCount} / ${total} vehicles`}
          icon={CheckCircle2}
          color="green"
        />
        <MetricCard
          title="Overdue"
          value={overdueCount}
          subtitle="Past grace period"
          icon={AlertTriangle}
          color="red"
        />
        <MetricCard
          title="Flagged This Month"
          value={flaggedRows.length}
          subtitle="Needs attention"
          icon={AlertCircle}
          color="amber"
        />
        <MetricCard
          title="Open Exceptions"
          value={exceptions.length}
          subtitle="All periods"
          icon={Clock3}
          color="purple"
        />
      </div>

      {/* Compliance-calendar grid — count is the roster size for the selected period */}
      <Panel title="Compliance Calendar" count={total} icon={Calendar}>
        {sortedRows.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No active fleet vehicles.</p>
        ) : (
          <>
            {/* Mobile: cards — essential chase fields only */}
            <div className="space-y-3 md:hidden">
              {sortedRows.map((r) => (
                <button
                  key={r.fleet_vehicle_id}
                  type="button"
                  onClick={() => rowClick(r, clickCtx)}
                  className="w-full text-left p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{r.vehicle_number}</p>
                      <p className="text-xs text-gray-400 truncate">{r.vehicle_type_display}</p>
                    </div>
                    <StatusBadge status={r.row_status} />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-600 flex items-center gap-1.5">
                      <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{r.assignee_name || 'Unassigned'}</span>
                    </p>
                    <p className="text-xs text-gray-600 flex items-center gap-1.5">
                      <History className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span>
                        {r.last_done_date
                          ? `Last done ${formatDate(r.last_done_date)}`
                          : 'Never inspected'}
                      </span>
                    </p>
                    {formatKm(r.odometer_km) && (
                      <p className="text-xs text-gray-600 flex items-center gap-1.5">
                        <Gauge className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="tabular-nums">{formatKm(r.odometer_km)}</span>
                      </p>
                    )}
                    {r.odometer_review_required && (
                      <div className="pt-1">
                        <StatusBadge status="review_required" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop: dense ops table — sticky header, fixed columns (DS Pattern B) */}
            <div className="hidden md:block">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[18%]" />
                  <col className="w-[20%]" />
                  <col className="w-[16%]" />
                  <col className="w-[16%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[4%]" />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide shadow-[0_1px_0_0_theme(colors.gray.200)]">
                    <th className="px-3 py-3">Vehicle</th>
                    <th className="px-3 py-3">Assignee</th>
                    <th className="px-3 py-3">Last done</th>
                    <th className="px-3 py-3">This month</th>
                    <th className="px-3 py-3 text-right">Odometer</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-2 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedRows.map((r) => {
                    const doneThisMonth = Boolean(r.inspection_date);
                    const odo = formatKm(r.odometer_km);
                    return (
                      <tr
                        key={r.fleet_vehicle_id}
                        onClick={() => rowClick(r, clickCtx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            rowClick(r, clickCtx);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`${r.vehicle_number}, ${r.row_status}`}
                        className="hover:bg-gray-50 cursor-pointer transition-colors focus-visible:outline-none focus-visible:bg-red-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                      >
                        <td className="px-3 py-3">
                          <div className="font-semibold text-gray-900 truncate">{r.vehicle_number}</div>
                          <div className="text-xs text-gray-400 truncate">{r.vehicle_type_display}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-gray-700 truncate">{r.assignee_name || '—'}</div>
                          {r.submitted_by_name && doneThisMonth && r.submitted_by_name !== r.assignee_name && (
                            <div className="text-xs text-gray-400 truncate">
                              Submitted by {r.submitted_by_name}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {r.last_done_date ? (
                            <span className="text-gray-700 whitespace-nowrap">{formatDate(r.last_done_date)}</span>
                          ) : (
                            <span className="text-gray-400">Never</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {doneThisMonth ? (
                            <span className="text-gray-700 whitespace-nowrap">{formatDate(r.inspection_date)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span className="tabular-nums">
                              {odo || <span className="text-gray-400">—</span>}
                            </span>
                            {r.odometer_review_required && (
                              <StatusBadge status="review_required" />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge status={r.row_status} />
                        </td>
                        <td className="px-2 py-3 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-300 inline" aria-hidden />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {/* Exceptions queue — 0 is intentional (confirms the queue is clear) */}
      <Panel title="Exceptions Queue" count={exceptions.length} icon={AlertTriangle}>
        {loadingExceptions ? (
          <p className="text-sm text-gray-500 text-center py-8">Loading...</p>
        ) : exceptions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            No open exceptions — every flagged item has been reviewed.
          </p>
        ) : (
          <ul className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
            {exceptions.map((insp) => {
              const flaggedItems = (insp.results || []).filter((r) => r.is_fail);
              const odometerNeedsReview = insp.odometer_status === 'review_required';
              return (
                <li key={insp.id}>
                  <button
                    type="button"
                    onClick={() => setDetailModal({ open: true, inspectionId: insp.id })}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-gray-900">{insp.vehicle_number}</span>
                        <span className="text-xs text-gray-400 truncate">{insp.vehicle_type_display}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                        <span className="truncate">
                          {[
                            ...flaggedItems.map((f) => f.item_label),
                            ...(odometerNeedsReview ? ['Odometer reading'] : []),
                          ].join(', ') || 'Flagged item(s)'}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span className="whitespace-nowrap text-gray-400">{formatDate(insp.inspection_date)}</span>
                      </div>
                    </div>
                    {odometerNeedsReview ? (
                      <StatusBadge status="review_required" />
                    ) : (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 whitespace-nowrap">
                        <ImageIcon className="w-3 h-3" />
                        {flaggedItems.reduce((sum, f) => sum + (f.photos?.length || 0), 0)}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <VehicleInspectionDetailModal
        isOpen={detailModal.open}
        onClose={() => setDetailModal({ open: false, inspectionId: null })}
        inspectionId={detailModal.inspectionId}
        onViewVehicle={(assetId) => {
          handleViewVehicle(assetId);
          setDetailModal({ open: false, inspectionId: null });
        }}
      />
    </div>
  );
};

export default AssetAdminFleetInspections;
